<!--
@component
RootLayout

Root layout. Tracks window size in appState and starts the data Poller.
-->

<script lang="ts">
  import { onMount } from 'svelte';
  import type { Snippet } from 'svelte';
  import { Poller, getData } from '$utils/data';
  import { appState } from '$lib/stores/index.svelte';
  import { page, updated } from '$app/state';
  import { beforeNavigate } from '$app/navigation';
  import { browser } from '$app/environment';
  import {
    reFetchUpdateSTLF,
    MetaAndHeaders,
  } from '$lib/components/ST/index.js';

  interface Props {
    children: Snippet;
  }

  let { children }: Props = $props();

  let innerWidth = $state(0);

  $effect(() => {
    appState.windowWidth = innerWidth;
  });

  let poller = $state<Poller | null>(null);

  onMount(() => {
    reFetchUpdateSTLF();
  });

  // `appState.optaData` is seeded by `+layout.ts` before this component
  // mounts. Here we only start the background `Poller` that refreshes it
  // on an interval (see src/utils/data/poller.js).
  $effect(() => {
    poller = new Poller((newData) => {
      appState.optaData = newData;
    });
    return () => poller?.destroy();
  });

  $effect(() => {
    const pageParams = browser && page?.url?.searchParams;
    appState.isLoading = !pageParams;
  });

  // Mirror SvelteKit's URL into the store so derived values (and the
  // few utils that need it during SSR) can read it without subscribing
  // to `page` themselves. Lives in the root layout so both the app and
  // embed groups get it.
  $effect(() => {
    appState.currentUrl = page.url ?? null;
  });

  // Stale-tab recovery — see #73.
  // `true` while we believe the bundle matches the deployed version.
  // Flipped to `false` during a re-check after the tab regains
  // attention; if the check throws (network blip on resume), stays
  // `false` so the next click hard-navs instead of soft-navving into
  // a possibly-missing chunk.
  let versionConfirmedFresh = $state(true);

  beforeNavigate((nav) => {
    if (nav.willUnload || !nav.to) return;
    if (updated.current || !versionConfirmedFresh) {
      nav.cancel();
      location.href = nav.to.url.href;
    }
  });

  $effect(() => {
    if (!browser) return;
    let reloading = false;
    const check = async (becomingVisibleAgain: boolean) => {
      if (document.visibilityState !== 'visible' || reloading) return;
      if (becomingVisibleAgain) versionConfirmedFresh = false;
      try {
        const stale = await updated.check();
        if (stale) {
          reloading = true;
          location.reload();
          return;
        }
        versionConfirmedFresh = true;
      } catch {
        // Network blip on resume — `versionConfirmedFresh` stays
        // `false` so the next click hard-navs.
      }
    };
    let mounted = false;
    const reCheck = () => check(mounted);
    document.addEventListener('visibilitychange', reCheck);
    window.addEventListener('focus', reCheck);
    window.addEventListener('pageshow', reCheck);
    void check(false);
    mounted = true;
    return () => {
      document.removeEventListener('visibilitychange', reCheck);
      window.removeEventListener('focus', reCheck);
      window.removeEventListener('pageshow', reCheck);
    };
  });
</script>

<svelte:window bind:innerWidth />

{@render children()}

<MetaAndHeaders />
